import React, { useCallback, useRef } from 'react'
import {
  LayoutBand,
  Row,
  useOverlay,
  IconButton,
  useContainerWidth,
  colors,
  Separator,
  Cell,
  Grid,
  type useInfoSheet,
} from '@fs/zion-ui'
import { Person } from '@fs/zion-person'
import { TreePedigree, MenuMore } from '@fs/zion-icon'
import type { Person as PersonType } from '@fs/zion-tree-types'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { useNonReligiousMode } from '@fs/zion-permissions'
import { useFeatureFlag } from '@fs/zion-flags'
import { TempleIndicator } from '@fs/zion-ordinances-reserve'
import ErrorBoundary from '@fs/zion-error-boundary'
import { PersonQualityScoreButton } from '@fs/zion-person-quality-score'
import { usePersonPageParams } from '../../personPageRouteUtil'
import { useShowTempleContext } from '../../../providers/ShowTempleContext'
import useOrdinances from '../../../data-service/useOrdinances'
import usePrimaryParents from '../../../data-service/usePrimaryParents'
import { useRefreshFamilyOrdinancesPeople } from '../../../data-service/useFamilyOrdinances'
import { mutateFocusPerson } from '../../../data-service/useFocusPerson'
import MobileMenu from '../mobile-menu/MobileMenu'
import ResearchHelpIndicator from './ResearchHelpIndicator'
import PortraitSelectorButton from './PortraitSelectorButton'
import ColorStripe from './ColorStripe'
import BannerActions from './BannerActions'
import useDisplayableOrdinances from '../ordinances/useDisplayableOrdinances'
import usePersonQualityScore from '../../../data-service/usePersonQualityScore'
import { CLASSIC_GUTTERS } from '../../constants'

const marginOverrideCss = css`
  button,
  a {
    margin: 0;
  }
`

const alignCss = css`
  /* Start margin to line up the actions with the person details above it */
  margin-inline-start: var(--start-padding);
`

type Props = {
  focusPerson: PersonType | undefined
  isMobile: boolean
  scoreInfoSheet: ReturnType<typeof useInfoSheet>
}

export default function PersonBanner({ focusPerson, isMobile, scoreInfoSheet }: Props): JSX.Element {
  const [t] = useTranslation()
  const mobileMenuOverlay = useOverlay()
  const { pid } = usePersonPageParams('full')
  const atSize = useContainerWidth()
  const buttonSize = atSize({ default: 'xs', xs: 'sm', sm: 'md' })
  const componentSize = atSize({ default: 'sm', xxs: 'md', md: 'lg' })
  const smallMobile = atSize({ default: true, xs: false })
  const showActions = atSize({ default: false, sm: true })
  const avatarSize = atSize({ default: 'sm', sm: 'md', md: 'lg' })
  const { score, loading: loadingPqs, showPersonQualityScore } = usePersonQualityScore()
  const pqsHasTracked = useRef(false)

  return (
    <ErrorBoundary>
      <div
        style={
          !isMobile
            ? {
                /* Removes horizontal scrollbar. See https://familysearch.slack.com/archives/CMLL2NGUF/p1680197930194719 */
                overflow: 'hidden',
              }
            : undefined
        }
      >
        <ColorStripe gender={focusPerson?.gender} />
        <LayoutBand maxWidth="lg" color={colors.gray00} marginY={CLASSIC_GUTTERS} marginX={CLASSIC_GUTTERS}>
          {isMobile ? (
            <div style={{ minHeight: '64px' }}>
              <Row gutters="xxs">
                {/* eslint-disable-next-line test-selectors/button -- NA */}
                <PortraitSelectorButton alignY="middle" noShrinkX focusPerson={focusPerson} size={avatarSize} />
                <h1 alignY="middle">
                  <Person
                    personObj={focusPerson ?? { name: t('tree-person.unknown.label'), id: pid, unknown: true }}
                    hideAvatar
                    componentSize={componentSize}
                    lifespanSize="short"
                    doNotLinkToPersonPage
                  />
                </h1>

                <div growX />

                {focusPerson && !smallMobile && (
                  <IndicatorsRow noShrinkX buttonSize={buttonSize} focusPerson={focusPerson} />
                )}
                <Cell noShrinkX>
                  <Row gutters="xxs">
                    {focusPerson && !focusPerson.deleted && !showActions && (
                      <div noShrinkX css={marginOverrideCss}>
                        <IconButton
                          title={t('tree-person.view-tree.action')}
                          data-testid="mobile-view-tree-button"
                          linkName="Mobile View Tree Icon"
                          Icon={TreePedigree}
                          size={buttonSize}
                          to={`/tree/pedigree/${pid}`}
                          external
                        />
                      </div>
                    )}
                    <div noShrinkX css={marginOverrideCss}>
                      <IconButton
                        title={t('tree-person.open-menu.action')}
                        data-testid="mobile-menu-button"
                        linkName="Mobile Menu Button"
                        size={buttonSize}
                        onClick={mobileMenuOverlay.handleClick}
                        Icon={MenuMore}
                      />
                    </div>
                  </Row>

                  {focusPerson && smallMobile && (
                    <Cell>
                      <Separator size="nano" />
                      <IndicatorsRow buttonSize={buttonSize} focusPerson={focusPerson} />
                    </Cell>
                  )}
                </Cell>
              </Row>

              <Grid gutters="nano">
                {focusPerson && showPersonQualityScore && (
                  <>
                    <Separator size={atSize({ default: 'xxs', xs: 'hide' })} />
                    <div
                      css={alignCss}
                      style={{
                        '--start-padding': atSize({
                          default: 'calc(40px + var(--spacer-xxs))',
                          sm: 'calc(64px + var(--spacer-xxs))',
                          md: 'calc(88px + var(--spacer-xxs))',
                        }),
                      }}
                    >
                      <PersonQualityScoreButton // eslint-disable-line test-selectors/button -- NA
                        score={score}
                        loading={loadingPqs}
                        infoSheetOverlay={scoreInfoSheet}
                        pqsHasTracked={pqsHasTracked}
                      />
                    </div>
                  </>
                )}
                {focusPerson && showActions && (
                  <div
                    css={alignCss}
                    style={{
                      '--start-padding': atSize({
                        default: 'calc(40px + var(--spacer-xxs) - 8px)',
                        sm: 'calc(64px + var(--spacer-xxs) - 8px)',
                        md: 'calc(88px + var(--spacer-xxs) - 8px)',
                      }),
                    }}
                  >
                    <BannerActions focusPerson={focusPerson} hideText={atSize({ default: true, md: false })} />
                  </div>
                )}
              </Grid>
            </div>
          ) : (
            <>
              <Row alignY="middle">
                {/* eslint-disable-next-line test-selectors/button -- NA */}
                <PortraitSelectorButton noShrinkX focusPerson={focusPerson} size="lg" />
                <h1>
                  <Person
                    hideAvatar
                    personObj={
                      focusPerson ?? {
                        name: t('tree-person.unknown.label'),
                        id: pid,
                        unknown: true,
                      }
                    }
                    componentSize="xl"
                    lifespanSize="long"
                    doNotLinkToPersonPage
                  />
                </h1>
              </Row>
              {focusPerson && (
                <div css={alignCss} style={{ '--start-padding': 'calc(88px + var(--spacer-sm) - 8px)' }}>
                  <BannerActions focusPerson={focusPerson} />
                </div>
              )}
            </>
          )}
        </LayoutBand>
      </div>
      {isMobile && <MobileMenu overlay={mobileMenuOverlay} showAdditionalActions={!showActions} />}
    </ErrorBoundary>
  )
}

function TempleIndicatorWrapper({
  focusPerson,
  size,
}: {
  focusPerson: PersonType
  size: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
}): JSX.Element | null {
  const primaryParents = usePrimaryParents(focusPerson.id)
  const { ordinances, mutateOrdinances } = useOrdinances()
  const displayedOrdinances = useDisplayableOrdinances(ordinances, primaryParents)
  const { isOn: showFamilyOrdinances } = useFeatureFlag('treeWebTx_familyOrdinanceView')

  const refreshFamilyOrdinancesPeople = useRefreshFamilyOrdinancesPeople()
  const refetchWithFamilies = useCallback(
    async (personIds: string[]) => {
      await mutateOrdinances()
      await Promise.all([
        mutateFocusPerson(focusPerson.id),
        refreshFamilyOrdinancesPeople(personIds.filter((id) => id !== focusPerson.id)),
      ])
    },
    [focusPerson.id, mutateOrdinances, refreshFamilyOrdinancesPeople]
  )

  if (!displayedOrdinances) {
    return null
  }

  return (
    <TempleIndicator
      person={focusPerson}
      ordinances={displayedOrdinances}
      refetchTempleRollupStatus={showFamilyOrdinances ? refetchWithFamilies : undefined}
      size={size}
      templeRollupStatus={focusPerson.templeRollupStatus}
    />
  )
}

type IndicatorsRowProps = {
  focusPerson: PersonType
  buttonSize: 'lg' | 'md' | 'xs' | 'sm' | 'xl'
}

function IndicatorsRow({ focusPerson, buttonSize }: IndicatorsRowProps): JSX.Element {
  const [showTemple] = useShowTempleContext()
  const [nonReligiousModeCheck, status, error] = useNonReligiousMode()
  const nonReligiousMode = status === 'loading' || error !== null || nonReligiousModeCheck

  return (
    <Row gutters="xxs">
      <ResearchHelpIndicator noShrinkX pid={focusPerson.id} focusPerson={focusPerson} size={buttonSize} />

      {!focusPerson.deleted &&
        !focusPerson.living &&
        !nonReligiousMode &&
        showTemple &&
        !focusPerson.researcherTree && <TempleIndicatorWrapper noShrinkX focusPerson={focusPerson} size={buttonSize} />}
    </Row>
  )
}
